import axios from 'axios';
import properties from '../properties';
import { generateReport } from './contractReportHelper';

export interface Metadata {
  field_name: string;
  field_description: string;
  key: string;
  value: string;
  evidence: any[];
}

export const getMetadataSchemaForContracts = () => {
  return [
    {
      key: 'effective_date',
      name: 'Effective Date',
      type: 'DATE',
      description: 'What is the effective date of the contract?',
    },
    {
      key: 'is_base_document',
      name: 'Document Type',
      type: 'BOOLEAN',
      description:
        'Is the document provided the base document of the contract (i.e., not an amendment)?',
    },
    {
      key: 'is_claims_administrator',
      name: 'Is Claims Administrator or Third Party Administrator',
      type: 'BOOLEAN',
      description:
        'Is the document a Claims Administrator or Third Party Administrator contract?',
    },
    {
      key: 'claims_administrator_name',
      name: 'Claims Administrator or Third Party Administrator',
      type: 'TEXT',
      description:
        'Who is the Claims Administrator or Third Party Administrator?',
    },
    {
      key: 'is_pharmacy_benefit_management',
      name: 'Is Pharmacy Benefit Management',
      type: 'BOOLEAN',
      description: 'Is the document a Pharmacy Benefit Management contract?',
    },
    {
      key: 'pharmacy_benefit_management_name',
      name: 'Pharmacy Benefit Management',
      type: 'TEXT',
      description: 'Who is the Pharmacy Benefit Management provider?',
    },
    {
      key: 'is_stop_loss_carrier',
      name: 'Is Stop Loss Carrier',
      type: 'BOOLEAN',
      description: 'Is the document a Stop Loss Carrier contract?',
    },
    {
      key: 'stop_loss_carrier_name',
      name: 'Stop Loss Carrier',
      type: 'TEXT',
      description: 'Who is the Stop Loss Carrier?',
    },
    {
      key: 'is_network_carrier',
      name: 'Is Network Carrier',
      type: 'BOOLEAN',
      description: 'Is the document a Network Carrier contract?',
    },
    {
      key: 'network_carrier_name',
      name: 'Network Carrier',
      type: 'TEXT',
      description: 'Who is the Network Carrier?',
    },
    {
      key: 'is_advisor_broker',
      name: 'Is Advisor / Broker',
      type: 'BOOLEAN',
      description: 'Is the document an Advisor or Broker contract?',
    },
    {
      key: 'advisor_broker_name',
      name: 'Advisor / Broker',
      type: 'TEXT',
      description: 'Who is the Advisor or Broker?',
    },
  ];
};

export const getMetadataSchemaForPlanDocs = () => {
  return [
    {
      key: 'plan_type',
      name: 'Plan Type',
      type: 'TEXT',
      description: 'What is the plan type of the provided document?',
    },
    {
      key: 'is_SPD',
      name: 'Document Type',
      type: 'BOOLEAN',
      description:
        'Is the document provided a Plan Document or Summary Plan Description',
    },
    {
      key: 'is_base_document',
      name: 'Document Type',
      type: 'BOOLEAN',
      description:
        'Is the document provided the base document of the contract (i.e., not an amendment)?',
    },
    {
      key: 'plan_start_date',
      name: 'Plan Start Date',
      type: 'DATE',
      description: "What's the start date of the plan?",
    },
    {
      key: 'plan_end_date',
      name: 'Plan End Date',
      type: 'DATE',
      description: 'Enter the end date of the plan?',
    },
  ];
};

export const formatMetadata = (inputArray: any[]) => {
  return inputArray.map((item) => {
    const value = item?.result;
    let formattedValue;
    switch (item?.type) {
      case 'BOOLEAN':
        // If the type is BOOLEAN and the value is null, set it to false
        formattedValue =
          typeof value === 'boolean'
            ? value
            : typeof value === 'string'
            ? ['true', 'yes']?.includes(value?.trim()?.toLowerCase())
            : false;
        break;

      case 'TEXT':
        formattedValue =
          value === null
            ? '-'
            : value?.toString()?.charAt(0)?.toUpperCase() +
              value?.toString()?.slice(1);
        break;

      case 'DATE':
        formattedValue = value === null ? '-' : value;
        break;

      default:
        formattedValue = value;
    }
    return {
      field_name: item?.name || item?.key,
      field_description: item?.description,
      key: item?.key,
      value: formattedValue,
      evidence: item.evidence || [],
    };
  });
};

export const extractMetadataFromContract = async (
  contractUrl: string,
  inputFileUrl: string,
) => {
  const requestBody = {
    prompt_conf: {
      system_instructions:
        'You are an expert information extraction algorithm designed to accurately extract attributes or answer questions from document. Your primary objective is to identify and retrieve only the relevant data points from the given text. If you encounter an attribute that is not explicitly stated or a question that cannot be answered from the content, you should return null for that specific attribute or question. This approach ensures that you provide accurate and complete information to the extent possible, while acknowledging any missing or unavailable data. Your task is to methodically scan the document, carefully extracting the pertinent details without making assumptions.',
    },
    document_file_conf: {
      file_url: contractUrl,
    },
    metadata_schema_file_conf: {
      file_url: inputFileUrl,
    },
    llm_model_conf: {
      llm_kwargs: {
        model_source: 'OpenAI',
        seed: 100,
      },
      llm_model_name: 'gpt-5-mini',
      llm_model_source: 'OpenAI',
    },
    batch_size: 60,
    num_iterations: 2,
  };

  try {
    const res = await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.extractMetadata,
      { llmInput: requestBody },
    );

    if (res.status === 200) {
      const answer = res?.data?.data?.answer;
      return answer;
    } else {
      throw new Error('Failed to retrieve data');
    }
  } catch (error) {
    throw error;
  }
};

export const extractCustomMetadataFromContract = async (
  contractUrl: string,
  question: string,
) => {
  const requestBody = {
    file_url: contractUrl,
    prompt_conf: {
      prompt_template:
        "Answer the question based only on the following document:\n\n{document}\n\nQuestion: {question}\n\nIf you don't know the answer, just say that you don't know, don't try to make up an answer.\n",
      system_instructions:
        'You are an expert AI context analysis assistant, highly skilled in answering questions across a wide range of topics. Your primary responsibility is to carefully read and understand the provided document, ensuring that your responses are accurate, concise, and directly based on the information within the document. Your answers should be clear, objective, and focused solely on the content of the provided text.',
    },
    question: question,
    llm_model_conf: {
      llm_kwargs: {
        model_kwargs: {
          response_format: {
            type: 'json_object',
          },
        },
        model_source: 'OpenAI',
        seed: 100,
      },
      llm_model_name: 'gpt-5-mini',
      llm_model_source: 'OpenAI',
    },
  };

  try {
    const res = await axios.post(
      properties.aiqBaseUrl + properties.contractBaseUrl + properties.chainDoc,
      { llmInput: requestBody },
    );

    if (res.status === 200) {
      const answer = res?.data?.data?.answer;
      return answer;
    } else {
      throw new Error('Failed to retrieve data');
    }
  } catch (error) {
    throw error;
  }
};

export const extractMetadataDocChain = async (
  contractUrl: string,
  inputFileUrl: string,
  model?: string,
  matrixSchema?: any,
) => {
  const requestBody = {
    prompt_conf: {
      system_instructions:
        '\nExtract attribute values or answers to questions from a given document, and provide supporting evidence, confidence score, and rationale for the score.\n\nYou will be provided with a document and a table in markdown format, listing attributes or questions with keys. Your task is to:\n\n- Extract the attribute values or answers directly from the document, ensuring they are concise and to-the-point. This means only providing exact values/names without additional context unless necessary.\n- Provide supporting evidence consisting of:\n  - An excerpt from which the answer was extracted.\n  - The section of the document where this excerpt is found.\n  - If the answer is supported by multiple excerpts, include all relevant evidence in an array.\n- Assign a confidence score (low, medium, or high) for each answer:\n  - low: The answer is vague, ambiguous, uncertain, or highly inferential.\n  - medium: The answer requires some interpretation or inference, but is reasonably supported.\n  - high: The answer is explicit and directly stated in the document.\n- For each confidence score, explain why this score is appropriate based on the document’s content. Justify your reasoning by referencing how clear, direct, or inferential the supporting evidence is.\n- Ensure all extracted values strictly derive from the document’s content. Return `null` if information is not available in the document for a given attribute or question.\n\n# Output Format\n\nThe output should be a JSON object structured as follows:\n- Result for each attribute or question includes "key", "result", "evidence", "confidence_score", and "score_rationale".\n- Follow the provided JSON format strictly.\n\n```json\n{{\n    "result": [\n        {{\n            "key": attribute_key_1,\n            "result": extracted_value,\n            "evidence": [\n                {{\n                    "excerpt": direct quotation from the document,\n                    "document_section": section name or number\n                }}\n            ],\n            "confidence_score": confidence_score,\n            "score_rationale": reason for the confidence score\n        }},\n        {{\n            "key": attribute_key_2,\n            "result": null,\n            "evidence": null,\n            "confidence_score": null,\n            "score_rationale": null\n        }}\n    ]\n}}\n```\n\n# Examples\n\n**Example Input**\n- Document: [Provide a summary or text of the document.]\n- Attributes/Questions Table:\n\n| key          | name               | description                                | type        |\n|--------------|--------------------|--------------------------------------------|-------------|\n| key_1        | Example Attribute  | Description of attribute 1.                | TEXT        |\n| key_2        | Another Attribute  | Description of attribute 2.                | NUMBER      |\n\n**Example Output**\n\n```json\n{{\n    "result": [\n        {{\n            "key": "key_1",\n            "result": "example response",\n            "evidence": [\n                {{\n                    "excerpt": "This is a direct quotation from the document.",\n                    "document_section": "Introduction"\n                }}\n            ],\n            "confidence_score": "high",\n            "score_rationale": "The data directly supports the extracted result."\n        }},\n        {{\n            "key": "key_2",\n            "result": null,\n            "evidence": null,\n            "confidence_score": null,\n            "score_rationale": null\n        }}\n    ]\n}}\n```\n\n**Note:** Respond concisely and precisely:\n- For attributes or questions requesting names, return only the names.\n- For those requesting values, return only the values, including any units necessary for clarity.\n- Do not include any additional text or explanations beyond what is required to convey the names or values.\n',
      prompt_template:
        "\nBased on the document:\n\n{document}\n\nAnd the attribute or question definitions:\n\n{metadata}\n\nExtract the result for each attribute or question using only the document's content.\n",
    },
    document_file_conf: {
      file_url: contractUrl,
    },
    ...(matrixSchema?.length > 0 && {
      metadata_schema: matrixSchema,
    }),
    ...(inputFileUrl?.length > 0 && {
      metadata_schema_file_conf: {
        file_url: inputFileUrl,
      },
    }),
    llm_model_conf: {
      llm_kwargs: {
        model_source: 'OpenAI',
        seed: 100,
      },
      llm_model_name: model ?? 'gpt-5-mini',
      llm_model_source: 'OpenAI',
    },
  };

  const landingaiRequestBody = {
    document_url: contractUrl,
    ...(matrixSchema?.length > 0 && {
      metadata_schema: matrixSchema,
    }),
    ...(inputFileUrl?.length > 0 && {
      metadata_schema_file_conf: {
        file_url: inputFileUrl,
      },
    }),
  };

  try {
    // Check user's preferred extraction method from persisted LoginPageStore (localStorage)
    let useLanding = true;
    try {
      const loginStoreString = localStorage.getItem('LoginPageStore');
      if (loginStoreString) {
        const parsed = JSON.parse(loginStoreString);
        const extractionMethod =
          parsed?.userResponse?.extractionMethod ?? parsed?.extractionMethod;
        if (
          extractionMethod &&
          typeof extractionMethod === 'string' &&
          !extractionMethod.trim().toLowerCase().includes('landing')
        ) {
          useLanding = false;
        }
      }
    } catch (err) {
      // ignore parse errors and proceed with default behavior
    }

    if (useLanding) {
      // Try Landing AI first, then fallback to extractMetadata if it fails
      try {
        const res = await axios.post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.landingAiExtract,
          landingaiRequestBody,
        );

        if (res.status === 200) {
          return res?.data?.data;
        }
        // if non-200, fall through to fallback call
      } catch (err) {
        // on error, fall through to fallback
      }

      const fallbackRes = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.extractMetadata,
        { llmInput: requestBody },
      );

      if (fallbackRes.status === 200) {
        return fallbackRes?.data?.data;
      }

      throw new Error(
        'Failed to retrieve data from Landing AI and fallback API',
      );
    } else {
      // Use extractMetadata API directly when user did not select Landing AI
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.extractMetadata,
        { llmInput: requestBody },
      );

      if (res.status === 200) {
        return res?.data?.data;
      }

      throw new Error('Failed to retrieve data from extractMetadata API');
    }
  } catch (error) {
    throw error;
  }
};

export const promptChain = async (
  prompt: any,
  contexts: any,
  documents?: any,
  modelName?: string,
  jsonSchemaFile?: string,
) => {
  const requestBody = {
    system_instructions:
      'You are an expert AI context analysis assistant, highly skilled in answering questions across a wide range of topics. Your primary responsibility is to carefully read and understand the provided document, ensuring that your responses are accurate, concise, and directly based on the information within the document. Your answers should be clear, objective, and focused solely on the content of the provided text.',
    prompt_template: prompt,
    ...(documents ? { documents: documents } : {}),
    contexts: contexts,
    ...(jsonSchemaFile
      ? {
          output_schema_file_conf: {
            file_url: jsonSchemaFile,
          },
        }
      : {}),
    llm_model_conf: {
      llm_kwargs: {
        model_kwargs: {
          response_format: {
            type: 'json_object',
          },
        },
        temperature: 1,
        model_source: 'OpenAI',
        seed: 100,
      },
      llm_model_name: modelName ?? 'gpt-5-mini',
      llm_model_source: 'OpenAI',
    },
  };

  const maxRetries = 3;
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await axios.post(
        properties.aiqBaseUrl + properties.contractBaseUrl + '/prompt-chain',
        { llmInput: requestBody },
      );

      const answer = res?.data?.data?.answer;

      if (res.status === 200 && answer) {
        return answer;
      }
    } catch {
      // no-op to avoid logging
    }

    if (attempt < maxRetries) {
      await delay(1000);
    }
  }

  throw new Error(
    'Failed to retrieve valid data from /prompt-chain after 3 attempts',
  );
};

export const convertToJSONString = (data: any) => {
  const modifiedString = JSON.stringify(data)
    .replace(/"/g, '\\"') // Escape quotes
    .replace(/\n/g, '\\n'); // Escape newlines

  // Wrap the escaped string in quotes
  return `"${modifiedString}"`;
};

export const exclusionsPrompts = {
  prompt:
    'Analyze the provided \n\n{document}\n\n and extract all explicitly mentioned excluded services that are not covered under this plan. Each exclusion should be represented as a JSON object with the following fields:\n\nexclusion_name: The name or title of the exclusion.\ndescription: The detailed explanation or description of the exclusion as mentioned in the document.\nThe output should be a JSON array where each object corresponds to one exclusion. Refer to the \n\n{sample}\n\n output JSON format below to structure your response',
  contexts: {
    sample: convertToJSONString([
      {
        exclusion_name: 'Experimental or Investigational Services',
        description:
          'Services considered experimental or investigational by FDA standards or lacking widely accepted medical backing are excluded.',
      },
      {
        exclusion_name: 'Cosmetic Surgery',
        description:
          'Procedures performed primarily to improve appearance are not covered unless deemed medically necessary.',
      },
    ]),
  },
};

export const exclusionsCodesPrompts = {
  prompt:
    "Using the provided JSON array of exclusions in the field \n\n{exclusions}\n\n, identify and map relevant HCPCS and CPT codes for each exclusion. For each exclusion in the input, generate one or more JSON objects with the following fields:\ncode: The HCPCS or CPT code relevant to the exclusion.\ncode_type: Specify the type of code ('HCPCS' or 'CPT').\ndescription: Provide a brief description of the procedure, service, or item associated with the code.\nevidence: Extract and include the specific text or excerpt from the exclusion's description that supports the exclusion.\nreasoning: Provide a concise analysis explaining why the code is excluded based on the evidence.\nexclusion_name: Name of the exclusion_name from exclusions \n\nUse the following example as a guide for the expected relationship\n\n sample input: {input_example}\n\n sample output : {output_example}\n\nThe output should strictly follow the formatting and structure of the sample output. Ensure each exclusion is processed to generate one or more JSON objects corresponding to relevant codes. Align the reasoning and evidence with the description provided for each exclusion.",
  contexts: {
    input_example: convertToJSONString([
      {
        exclusion_name: 'Cosmetic Surgery',
        description:
          'Procedures performed primarily to improve appearance, such as chemical peels, rhinoplasty, and liposuction, are not covered unless deemed medically necessary.',
      },
      {
        exclusion_name: 'Durable Medical Equipment',
        description:
          'Devices such as braces, support garments, and therapeutic devices are excluded unless medically necessary.',
      },
    ]),
    output_example: convertToJSONString([
      {
        code: '15788',
        code_type: 'CPT',
        description: 'Chemical peel, facial',
        evidence:
          'Procedures performed primarily to improve appearance, such as chemical peels, rhinoplasty, and liposuction, are not covered unless deemed medically necessary.',
        reasoning:
          'Code 15788 corresponds to chemical peels, which are excluded unless deemed medically necessary.',
        exclusion_name: 'Cosmetic Surgery',
      },
      {
        code: '15830',
        code_type: 'CPT',
        description:
          'Excision, excessive skin and subcutaneous tissue (including lipectomy), abdomen',
        evidence:
          'Procedures performed primarily to improve appearance, such as chemical peels, rhinoplasty, and liposuction, are not covered unless deemed medically necessary.',
        reasoning:
          'Code 15830 aligns with exclusions for liposuction unless it is for reconstructive or medically necessary purposes.',
        exclusion_name: 'Cosmetic Surgery',
      },
      {
        code: '19318',
        code_type: 'CPT',
        description: 'Reduction mammoplasty',
        evidence:
          'Procedures performed primarily to improve appearance, such as chemical peels, rhinoplasty, and liposuction, are not covered unless deemed medically necessary.',
        reasoning:
          'Code 19318 corresponds to cosmetic breast reduction, which is excluded unless deemed medically necessary.',
        exclusion_name: 'Cosmetic Surgery',
      },
      {
        code: 'E1399',
        code_type: 'HCPCS',
        description: 'Durable medical equipment, miscellaneous',
        evidence:
          'Devices such as braces, support garments, and therapeutic devices are excluded unless medically necessary.',
        reasoning:
          'Code E1399 represents miscellaneous durable medical equipment, which is excluded unless medically necessary.',
        exclusion_name: 'Durable Medical Equipment',
      },
      {
        code: 'L1845',
        code_type: 'HCPCS',
        description: 'Knee orthosis, rigid, adjustable knee joints',
        evidence:
          'Devices such as braces, support garments, and therapeutic devices are excluded unless medically necessary.',
        reasoning:
          'Code L1845 corresponds to knee orthoses, aligning with the exclusion of braces unless medically necessary.',
        exclusion_name: 'Durable Medical Equipment',
      },
      {
        code: 'A9270',
        code_type: 'HCPCS',
        description: 'Non-covered items or services',
        evidence:
          'Devices such as braces, support garments, and therapeutic devices are excluded unless medically necessary.',
        reasoning:
          'Code A9270 applies to non-covered items or services, consistent with the exclusion of therapeutic devices unless deemed necessary.',
        exclusion_name: 'Durable Medical Equipment',
      },
    ]),
  },
};

export const recommedationsPrompt = {
  prompt:
    'Using the evaluation procedure mentioned in process: {document2}, evaluate agreement:\n\n{document1}\n\n and create a actionable comments based on the analysis. Divide the analysis into 2 parts. First is where you can give recommendation based on available information in agreement and Second part comments and recommendation where you need to seek for extra information. Do not repeat recommendation or comment which are mentioned in part 1 to make the output concise. Add evidence from the agreement where possible to support the issue and recommendation. Provide output JSON same as {sample_output}',
  contexts: {
    sample_output: convertToJSONString({
      analysis: {
        part1: {
          recommendations_based_on_available_information: [
            {
              proposal: 'Benchmark ASO Fees Against Industry Standards',
              issue:
                'The ASO fees for the Choice + and Choice + HSA plans are $37.50PEPM in Year1 increasing to $39.84 PEPM by Year 3. The Surest plan fees are even higher,starting at $57.00PEPM and increasing to $60.47 PEPM by Year 3. These fees exceed the typical industry range of $25–$35 PEPM.',
              recommendation:
                ': Negotiate to bring the ASO fees within the industry standard range. Request justification for the higher fees, especially for the Surest plan, and ensure that the fees are competitive and reasonable based on the services provided.',
              evidence:
                "Exhibit D – Fees: 'ASO Fees (PEPM)...Choice + 37.50...Surest37.50...Surest57.00'",
            },
          ],
        },
        part2: {
          comments_and_recommendations_for_extra_information: [
            {
              proposal: 'Clarify Seed Money Requirements',
              issue:
                "The agreement mentions that the customer must maintain an 'Account Balance' to cover expected activity, but the specific amount and terms are not detailed.",
              evidence:
                "Exhibit A – Medical Benefit Administration Services: 'Customer will maintain a Bank Account balance to cover expected Bank Account activity (the 'Account Balance').",
              recommendation:
                'Request detailed information on the required Account Balance, including the calculation method, amount, and any conditions for adjustments. Negotiate to limit the seed money to a reasonable amount (typically one week to one month of expected claims) and ensure clear accounting and terms for its return.',
            },
          ],
        },
      },
    }),
  },
};

export const commercialEvaluationPrompt = {
  prompt: `Task: I will upload a contract/agreement:{document} containing financial charges and fee details.\n\n\
Your task is to extract, categorize, and structure all fees and financial elements into predefined categories in JSON format.\n\n\
Do not include categories that are empty (except for 'Run Off Claims Processing', which must always be shown).\n\
Ensure that all category names are stored as strings in the JSON output.\n\n\
All fees must be formatted as strings with appropriate units (e.g., '$108.12 PEPM', '$958 per case', '19.5% of recovered amount').\n\
Summarize financial risks or key insights in the description of each category where applicable.\n\
Keep descriptions short (less than 2-3 lines).\n\n\
If summation of fees is possible within a category, include it in the description.\n\
Example:\n\
✅ '$52.32 PEPM ($52.10+$0.22)' when summation is meaningful.\n\
❌ No summation when some fees are per case, per consultation, or per claim (as they are not directly additive).\n\n\
Split the categories into three sections:\n\n\
- 'Fee_Categories_with_benchmark' (Categories that require benchmarks).\n\
- 'Fee_Categories_wo_benchmark' (Categories that do not require benchmarks).\n\
- 'financial_opportunities' (Includes vendor-provided credits, allowances, and performance guarantees).\n\n\
For the categories 'Fixed Monthly Fees (PEPM)', 'As Invoiced (% Based Fees)', and 'Run Off Claims Processing', include a 'benchmark' field for each service.\n\n\
**Benchmark Details:**\n\n\
- **Fixed Monthly Fees (PEPM)(do not show benchmark if benchmark for the service is missing below, just leave that blank):**\n\
  - Administration fee: '$35 PEPM'\n\
  - Reporting Fee: '$1.75 PEPM'\n\
  - Analytics Fee: '$1.59 PEPM'\n\
  - PBM Integration Fee: '$3 PEPM'\n\
  - Stop Loss Coordination: '$1.46 PEPM'\n\
  - Behavioral Health: '$1 PEPM'\n\
  - Care Management: '$2.5 PEPM'\n\
  - Utilization Management: '$3.5 PEPM'\n\
  - Broker Fees: '$7 PEPM'\n\
  - FSA/HSA Administration Fees: '$2.5 PEPM'\n\
  - Wellness Service: '$3.5 PEPM'\n\
  - Network access or provider network fees: '$16.67 PEPM'\n\
  - Virtual Health or Telemedicine: '$1 PEPM'\n\
- **As Invoiced (% Based Fees)-Benchmarks(do not show benchmark if benchmark for the service is missing below, just leave that blank):**\n\
  - Shared Savings: '25% of savings'\n\
  - Subrogation Recovery: '37.5% of recovery'\n\
  - Claim Code Review: '37.5% of savings'\n\
  - Payment Integrity: '35% of savings'\n\
  - Out of Network savings: '30% of savings'\n\
- **Run Off Claims Processing:**\n\
  - Benchmark: 'Catered for 12 months post termination with fees included in the base fee.'\n\
  - Always show this category even if it's not explicitly mentioned in the agreement.\n\
  - Recommend including run-off claims processing as a service in the description if it is missing.\n\n\
For 'financial_opportunities', list it as part of 'Fee_Categories'.\n\
Each opportunity will be a category, and 'types' will be a list under each category.\n\n\
Fee_Categories_with_benchmark\nThese are categories where benchmark comparisons are provided for each service.\n\nFixed Monthly Fees (PEPM/PMPM)\n\ncategory: \"Fixed Monthly Fees (PEPM)\"\nDescription should include:\nA concise total of all PEPM/PMPM-based charges if summation is possible.\nA brief mention that these are recurring fixed fees charged per employee per month (PEPM) or per member per month (PMPM).\nIf the list of services include wellness, telehealth, maternity or similar services that are not required for administering the health plan such as administration, broker, stop loss and pbm integration, reporting, analytics, network access or fsa/hsa administration - suggest to review prior year utilization. If utilization is not good, suggest to move the service fee based on per case basis, so that it is billed based on utilization. \nEach service must include a \"benchmark\" comparing the service fee with industry standards.\n\nAs Invoiced (% Based Fees)\n\ncategory: \"As Invoiced (% Based Fees)\"\nDescription should include:\nA short note that charges depend on recovery amounts, so it recommended to negotiate them below 25%.\nAlso, a recommendation to set a cap to limit risk of $10,000 per recovery.\nEach service must include a \"benchmark\" comparing the percentage fee to industry standards.\n\nRun Off Claims Processing\n\ncategory: \"Run Off Claims Processing\"\nDescription should include:\n\"Recommend including run-off claims processing for 12 months post-termination as part of the agreement.\"\nAlways include this category, even if not addressed in the agreement.\nFee Field: Include specific details from the agreement, such as the number of months or years post-termination and whether the fee is included in the base fees or at an additional cost.\nBenchmark: \"Catered for 12 months post termination with fees included in the base fee.\"\n\nFee_Categories_wo_benchmark\nThese are categories where benchmarks are not applicable.\n\nUsage-Based Fees (Consultation & Care Services)\n\ncategory: \"Usage-Based Fees (Consultation & Care Services)\"\nDescription should include:\nA short note stating that charges are per consultation or per case and only incurred when services are used.\nServices should list the details of the services and fees.\n\nUsage-Based Fees (Administrative Services)\n\ncategory: \"Usage-Based Fees (Administrative Services)\"\nDescription should include:\nA concise total summing up all admin-based fees if possible.\n\nAs Invoiced (Fixed Amount Fees)\n\ncategory: \"As Invoiced (Fixed Amount Fees)\"\nDescription should include:\nA brief summary of fixed invoiced fees.\n\nOther\n\ncategory: \"Other\"\nDescription should include:\n\"Covers any additional financial implications or fees not explicitly categorized.\"\nAlways show this category to ensure all financial implications are captured.\n\nfinancial_opportunities\nThis section includes vendor-provided credits, allowances, and performance guarantees.\n\nCredits and Allowances\n\nIncludes audit credits, wellness credits, implementation credits, etc.\n\nPerformance Guarantees\n\nEach guarantee must specify both the commitment and the penalty.\n\nFinal Instructions for the AI Model:\nExtract all fees and categorize them accordingly.\nDrop any category that does not contain fees or financial implications, except for \"Run Off Claims Processing\" (always include).\nSplit the categories into:\n\"Fee_Categories_with_benchmark\"\n\"Fee_Categories_wo_benchmark\"\n\"financial_opportunities\"\nUse \"type\" and \"description\" for all entries under \"financial_opportunities\".\nEnsure \"Services\" are properly listed under each fee category.\nPreserve original fee values and ensure proper formatting.
Ensure that all extracted fees are categorized accordingly and strictly follow the JSON format as {sample_output}.`,
  contexts: {
    sample_output: convertToJSONString({
      fee_categories: {
        fee_categories_with_benchmark: [
          {
            category: 'Fixed Monthly Fees (PEPM)',
            description:
              '$ PEPM (Recurring fixed fees charged per employee per month, covering admin and network services.)',
            services: [
              {
                service_name: 'PPO',
                fee: '$ PEPM',
                benchmark:
                  '$30-$40 PEPM (includes claims administration and utilization management)',
              },
            ],
          },
          {
            category: 'As Invoiced (% Based Fees)',
            description:
              'Charges depend on recovery amounts. Recommend setting a cap to limit risk.',
            services: [
              {
                service_name: 'Fee for Subrogation Services',
                fee: '25% of gross subrogation recovery',
                benchmark: 'Less than 25% with a cap of $10,000 per claim.',
              },
              {
                service_name:
                  'Fee for Overpayment Identification, Prevention, and Claims Prepayment Analysis Activities',
                fee: '25% of the amount recovered',
                benchmark: 'Less than 25% with a cap of $10,000 per claim.',
              },
            ],
          },
          {
            category: 'Run off Claims Processing',
            description:
              'Recommend including run-off claims processing for 12 months post-termination as part of the agreement.',
            services: [
              {
                service_name: 'Run off Claims Processing',
                fee:
                  '12 months for medical and vision, 3 months for pharmacy at no additional cost',
                benchmark:
                  'Catered for 12 months post termination with fees included in the base fee',
              },
            ],
          },
        ],
        fee_categories_wo_benchmark: [
          {
            category: 'Usage-Based Fees (Consultation & Care Services)',
            description:
              'Per consultation or per case; incurred only when used.',
            services: [
              {
                service_name: 'Fee for Independent Claims Review',
                fee: '$500.00 per independent review',
              },
            ],
          },
          {
            category: 'Usage-Based Fees (Administrative Services)',
            description: 'Summation of admin-based fees possible.',
            services: [
              {
                service_name: 'Fee for Ad Hoc Reports',
                fee: '$150.00 per hour',
              },
              {
                service_name: 'Fee for Article 11(e) Data Audits',
                fee: '$150.00 per hour',
              },
            ],
          },
          {
            category: 'As Invoiced (Fixed Amount Fees)',
            description: 'Summary of fixed invoiced fees.',
            services: [
              {
                service_name:
                  'Fee for Pharmacy Carve-out and Accumulation of Essential Health Benefits Cost Shares with Pharmacy Carve-out Vendor',
                fee: '$1.75 per Subscriber per month',
              },
            ],
          },
          {
            category: 'Other',
            description:
              'Covers any additional financial implications or fees not explicitly categorized.',
            services: [
              {
                service_name:
                  'Fee for Electronic Data Feeds to an Outside Vendor',
                fee: '$1,000.00 for each additional feed',
              },
            ],
          },
        ],
        financial_opportunities: [
          {
            category: 'Credits and Allowances',
            description:
              'Includes credits and allowances provided for wellness, audits, and implementations.',
            types: [
              {
                type: 'Enhanced Wellbeing Solutions Foundational Program',
                description: '$4.47 per Subscriber per month.',
              },
            ],
          },
        ],
      },
    }),
  },
};

export const formatandSaveFeeRecommendationsData = async (
  data: any,
  egNid: string,
  contractId: string,
  draftId?: string,
) => {
  const {
    fee_categories_with_benchmark,
    fee_categories_wo_benchmark,
    financial_opportunities,
  } = data?.answer?.fee_categories || data?.fee_categories || data?.answer;
  // const availableRecommendations =
  //   part1?.recommendations_based_on_available_information;
  // const additionalComments =
  //   part2?.comments_and_recommendations_for_extra_information;

  await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        '/add-fee-evaluations',
      {
        eg_nid: egNid,
        contract_id: contractId,
        analysis: {
          fee_categories_with_benchmark,
          fee_categories_wo_benchmark,
          financial_opportunities,
        },
        draft_id: draftId,
      },
    )
    .then((response) => {
      if (response.status === 200) {
      }
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {});
};

export const getDefaultUrlsForServiceAgreement = (
  serviceAgreementName: string,
) => {
  const serviceAgreementUrls: Record<string, any> = {
    'Third-Party Administrator (TPA)': {
      overview:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/ASA Metadata.csv',
    },
    'Stop-Loss Insurance Provider': {
      overview:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Stop Loss Metadata/Stop Loss Metadata.csv',
    },
  };

  return serviceAgreementUrls[serviceAgreementName];
};

export const getUrlsForServiceAgreement = (
  serviceAgreementName: string,
  analysis?: string[],
) => {
  const tpaURLs = {
    // overview:
    //   'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/ASA Metadata.csv',
    administratorObligations:
      'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Obligations of administrator.csv',
    sponsorObligations:
      'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Obligations of sponsor.csv',
    fees:
      'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Fees.csv',
  };

  const serviceAgreementUrls: Record<string, Record<string, string>> = {
    'Third-Party Administrator (TPA)': tpaURLs,
    'Administrative Services Only (ASO) Provider': tpaURLs,
    'Stop-Loss Insurance Provider': {
      overview:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Stop Loss Metadata/Stop Loss Metadata.csv',
      specificCoverageType:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Stop Loss Metadata/Specific Stop Loss - Coverage Type.csv',
      aggCoverageType:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Stop Loss Metadata/Aggregate Stop Loss - Coverage Type.csv',
      aggAttachmentFactors:
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Stop Loss Metadata/Aggregate Stop Loss Attachment Factors.csv',
    },
  };

  const analysisKeyMap: Record<string, Record<string, string[]>> = {
    'Third-Party Administrator (TPA)': {
      Obligations: ['administratorObligations', 'sponsorObligations'],
      Fees: ['fees'],
    },
    'Administrative Services Only (ASO) Provider': {
      Obligations: ['administratorObligations', 'sponsorObligations'],
      Fees: ['fees'],
    },
    'Stop-Loss Insurance Provider': {
      'Stop loss': [
        'specificCoverageType',
        'aggCoverageType',
        'aggAttachmentFactors',
      ],
    },
  };

  const urls = serviceAgreementUrls[serviceAgreementName];
  const keyMap = analysisKeyMap[serviceAgreementName];

  if (!analysis) {
    return urls; // Return all URLs if no analysis is provided
  }

  if (!urls || !keyMap || analysis?.length === 0) {
    return {};
  }

  const allowedKeys = new Set(analysis.flatMap((term) => keyMap[term] || []));
  allowedKeys.add('overview'); // Always include overview

  return Object.fromEntries(
    Object.entries(urls).filter(([key]) => allowedKeys.has(key)),
  );
};

//Functions to extract Service Agreements metadata
export const extractASOMetadata = async (
  documentUrl: string,
  urls: Record<string, any>,
  model?: string,
  matrixSchema?: any,
) => {
  let metadataResults: any = [];
  if (matrixSchema) {
    const extractedMetadata = await extractMetadataDocChain(
      documentUrl,
      '',
      model === 'fees' ? 'gpt-5-mini' : model,
      matrixSchema,
    );
    return {
      model,
      metadata: formatMetadata(extractedMetadata.answer),
    };
  } else {
    const metadataPromises = Object.entries(urls).map(async ([key, value]) => {
      try {
        const extractedMetadata = await extractMetadataDocChain(
          documentUrl,
          value,
          key === 'fees' ? 'gpt-5-mini' : model,
        );
        return {
          key,
          metadata: formatMetadata(extractedMetadata.answer),
        };
      } catch (error) {
        return { key, metadata: null };
      }
    });
    metadataResults = await Promise.all(metadataPromises);
  }

  // Run all metadata extraction APIs in parallel

  let overview: Metadata[] = [];
  let sponsorObligations: Metadata[] = [];
  let administratorObligations: Metadata[] = [];
  let feeStructures: Metadata[] = [];
  let matrix: Metadata[] = [];
  let fees: Metadata[] = [];

  metadataResults.forEach(({ key, metadata }: any) => {
    if (!metadata) {
      return;
    }
    switch (key) {
      case 'overview':
        overview = metadata;
        break;
      case 'sponsorObligations':
        sponsorObligations = metadata;
        break;
      case 'administratorObligations':
        administratorObligations = metadata;
        break;
      case 'feeStructures':
        feeStructures = metadata;
        break;
      case 'matrix':
        matrix = metadata;
        break;
      case 'fees':
        fees = metadata;
        break;
      default:
        break;
    }
  });

  return {
    overview,
    sponsorObligations,
    administratorObligations,
    feeStructures,
    matrix,
    fees,
  };
};

export const extractStoplossMetadata = async (
  documentUrl: string,
  urls: Record<string, any>,
) => {
  const metadataPromises = Object.entries(urls).map(async ([key, value]) => {
    try {
      const extractedMetadata = await extractMetadataDocChain(
        documentUrl,
        value,
      );
      return {
        key,
        metadata: formatMetadata(extractedMetadata.answer),
      };
    } catch (error) {
      return { key, metadata: null }; // Handle failure gracefully
    }
  });

  // Run all metadata extraction APIs in parallel
  const metadataResults = await Promise.all(metadataPromises);

  let stoplossOverview: Metadata[] = [];
  let aggregateStopLossCoverageType: Metadata[] = [];
  let aggregateStopLossAttachmentFactors: Metadata[] = [];
  let specificStopLossCoverageType: Metadata[] = [];

  metadataResults.forEach(({ key, metadata }) => {
    if (!metadata) {
      return;
    }
    switch (key) {
      case 'overview':
        stoplossOverview = metadata;
        break;
      case 'aggCoverageType':
        aggregateStopLossCoverageType = metadata;
        break;
      case 'aggAttachmentFactors':
        aggregateStopLossAttachmentFactors = metadata;
        break;
      case 'specificCoverageType':
        specificStopLossCoverageType = metadata;
        break;
      default:
        break;
    }
  });

  return {
    stoplossOverview,
    aggregateStopLossCoverageType,
    aggregateStopLossAttachmentFactors,
    specificStopLossCoverageType,
  };
};

//Functions to save Service Agreements metadata to db
export const addAdministrativeServicesMetadata = async (
  contractId: string,
  egNid: string,
  overview: Metadata[],
  sponsorObligations: Metadata[],
  administratorObligations: Metadata[],
  fees: Metadata[],
  draftId?: string,
) => {
  await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addAdministrativeMetadata,
      {
        contract_id: contractId,
        overview: overview,
        obligation_sponsor: sponsorObligations,
        obligation_administrator: administratorObligations,
        eg_nid: egNid,
        draft_id: draftId,
      },
    )
    .then((response) => {
      if (response.status === 200) {
      }
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {});
};

const addStoplossMetadata = async (
  contractId: string,
  overview: Metadata[],
  aggregateStopLossCoverageType: Metadata[],
  aggregateStopLossAttachmentFactors: Metadata[],
  specificStopLossCoverageType: Metadata[],
  egNid: string,
  draftId?: string,
) => {
  await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addStoplossMetadata,
      {
        contract_id: contractId,
        overview: overview,
        aggregateStopLossCoverageType: aggregateStopLossCoverageType,
        aggregateStopLossAttachmentFactors: aggregateStopLossAttachmentFactors,
        specificStopLossCoverageType: specificStopLossCoverageType,
        eg_nid: egNid,
        draft_id: draftId,
      },
    )
    .then((response) => {
      if (response.status === 200) {
      }
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {});
};

//Function to extract metadata based on service agreement type
export const extractAndSaveMetadataForContract = async (
  egNid: string,
  contractId: string,
  serviceAgreement: string,
  documentUrl: string,
  draftId?: string,
  featureUrls?: Record<string, any>,
) => {
  const urls = featureUrls || getUrlsForServiceAgreement(serviceAgreement);

  if (urls && documentUrl) {
    // Call the appropriate API based on the service agreement name
    switch (serviceAgreement) {
      case 'Third-Party Administrator (TPA)':
      case 'Administrative Services Only (ASO) Provider':
        const {
          overview = [],
          sponsorObligations = [],
          administratorObligations = [],
          fees = [],
        } = await extractASOMetadata(documentUrl, urls);
        await addAdministrativeServicesMetadata(
          contractId,
          egNid,
          overview,
          sponsorObligations,
          administratorObligations,
          fees,
          draftId,
        );
        break;
      case 'Stop-Loss Insurance Provider':
        const {
          stoplossOverview,
          aggregateStopLossCoverageType,
          aggregateStopLossAttachmentFactors,
          specificStopLossCoverageType,
        } = await extractStoplossMetadata(documentUrl, urls);
        await addStoplossMetadata(
          contractId,
          stoplossOverview,
          aggregateStopLossCoverageType,
          aggregateStopLossAttachmentFactors,
          specificStopLossCoverageType,
          egNid,
          draftId,
        );
        break;
      default:
        break;
    }
  }
};

export const extractAndSaveFees = async (
  documentUrl: string,
  contractId: string,
  egNid: string,
  type: string,
  draftId?: string,
) => {
  const defaltParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getDefaultParameters,
    {
      ...(contractId ? { contract_ids: [contractId] } : {}),
      service_agreement_name: type,
      parameter: 'Fees',
    },
  );
  const matrixSchema = defaltParameters.data?.data?.map((item: any) => {
    return {
      key: item.key,
      name: item.provision_name,
      description: item.assessment_description,
      type: item.response_type,
    };
  });
  // const feeUrls = [
  //   {
  //     fees:
  //       'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Fees.csv',
  //   },
  // ];

  const responses = await Promise.all(
    // feeUrls.map((feeUrl) =>
    [extractASOMetadata(documentUrl, [], 'gpt-5-mini', matrixSchema)],
    // ),
  );

  const fees = responses[0]?.fees ?? responses[0]?.metadata;

  try {
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addAdministrativeMetadata,
      {
        contract_id: contractId,
        matrix: fees,
        eg_nid: egNid,
        draft_id: draftId,
        service_agreement_name: type,
      },
    );
  } catch (error) {
    throw error;
  }
};

export const extractAndSaveSummary = async (
  documentUrl: string,
  contractId: string,
  egNid: string,
  draftId?: string,
) => {
  const defaltParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getSummaryOfFindingsParams,
  );
  const matrixSchema = defaltParameters.data?.data?.map((item: any) => {
    return {
      key: item?.key || '',
      name: item?.name || '',
      description: item?.prompt || '',
      type: 'TEXT',
    };
  });
  const responses = await Promise.all([
    extractASOMetadata(documentUrl, [], 'gpt-5-mini', matrixSchema),
  ]);

  const data = responses[0]?.metadata;

  try {
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.saveSummaryOfFindings,
      {
        ...(draftId ? { draft_id: draftId } : {}),
        ...(contractId ? { contract_id: contractId } : {}),
        data: data,
        eg_nid: egNid,
      },
    );
  } catch (error) {
    throw error;
  }
};

/*
export const extractAndSaveProvisionMatrix = async (
  documentUrl: string,
  contractId: string,
  egNid: string,
  type: string,
  draftId?: string,
  isDraft?: boolean,
) => {
  // let matrixUrls: any = [];
  const defaltParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getDefaultParameters,
    {
      contract_ids: [contractId],
      service_agreement_name: type,
      // parameter: 'Provision Matrix',
    },
  );
  const matrixSchema = defaltParameters.data?.data
    ?.filter((temp: any) => temp.clause !== 'Fees')
    .map((item: any) => {
      return {
        key: item.key,
        name: item.provision_name,
        description: item.assessment_description,
        type: item.response_type,
      };
    });
  // switch (type) {
  //   case 'Claims Administrator':
  //     matrixUrls = [
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Matrix_1.csv',
  //       },
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Matrix_2.csv',
  //       },
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Administrative Services Metadata/Matrix_3.csv',
  //       },
  //     ];
  //     break;
  //   case 'Pharmacy Benefit Management':
  //     matrixUrls = [
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/PBM Metadata/Matrix_1.csv',
  //       },
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/PBM Metadata/Matrix_2.csv',
  //       },
  //       {
  //         matrix:
  //           'https://storageblobprod.blob.core.windows.net/contract/static-uploads/PBM Metadata/Matrix_3.csv',
  //       },
  //     ];
  //     break;
  //   default:
  //     break;
  // }

  const responses = await Promise.all(
    // matrixUrls?.map((matrixUrl: any) =>
    [extractASOMetadata(documentUrl, [], 'gpt-5-mini', matrixSchema)],
    // ),
  );
  const matrix = responses?.reduce(
    (acc, response: any) => acc?.concat(response?.matrix ?? response?.metadata),
    [],
  );
  try {
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addAdministrativeMetadata,
      {
        matrix: matrix,
        eg_nid: egNid,
        ...(isDraft ? { draft_id: draftId } : { contract_id: contractId }),
        service_agreement_name: type,
      },
    );
  } catch (error) {
    throw error;
  }
};
*/

export const extractAndSaveProvisionMatrix = async (
  documentUrl: string,
  contractId: string,
  egNid: string,
  type: string,
  draftId?: string,
  isDraft?: boolean,
) => {
  const defaltParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getDefaultParameters,
    {
      contract_ids: contractId ? [contractId] : [],
      service_agreement_name: type,
    },
  );

  const matrixSchema = defaltParameters.data?.data?.map((item: any) => ({
    key: item.key ?? item?.field_name.toLowerCase().replace(/\s+/g, '_'),
    name: item.provision_name ?? item.field_name,
    description: item.assessment_description ?? item?.field_description,
    type: item.response_type,
  }));

  let matrix: any[] = [];
  let attempts = 0;

  while (attempts < 3) {
    try {
      const responses = await Promise.all([
        extractASOMetadata(documentUrl, [], 'gpt-5-mini', matrixSchema),
      ]);

      matrix = responses?.reduce(
        (acc, response: any) =>
          acc.concat(response?.matrix ?? response?.metadata),
        [],
      );

      if (matrix && matrix.length > 0) {
        break; // successful response, exit loop
      }
    } catch (error) {
      // Optionally log or handle the error
    }

    attempts++;
  }

  if (!matrix || matrix.length === 0) {
    return; // exit early
  }

  try {
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addAdministrativeMetadata,
      {
        matrix,
        eg_nid: egNid,
        ...(isDraft ? { draft_id: draftId } : { contract_id: contractId }),
        service_agreement_name: type,
      },
    );
  } catch (error) {
    throw error;
  }
};

export const addPlanExlusions = async (
  egNid: string,
  planId: string,
  exclusions: any[],
) => {
  await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        '/add-plan-exclusions',
      {
        eg_nid: egNid,
        plan_id: planId,
        exclusions: exclusions,
      },
    )
    .then((response) => {
      if (response.status === 200) {
      }
    });
};

export const extractAndSaveSPDProvisionMatrix = async (
  documentUrl: string,
  planId: string,
  egNid: string,
) => {
  const defaltParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getDefaultParameters,
    {
      service_agreement_name: 'Summary Plan Document',
      parameter: 'Coverage Check',
    },
  );
  const matrixSchema = defaltParameters.data?.data?.map((item: any) => {
    return {
      key: item.key,
      name: item.provision_name,
      description: item.assessment_description,
      type: item.response_type,
    };
  });

  const responses = await Promise.all([
    extractASOMetadata(documentUrl, [], 'gpt-5-mini', matrixSchema),
  ]);

  const matrix = responses?.reduce(
    (acc, response: any) => acc.concat(response?.matrix ?? response?.metadata),
    [],
  );
  try {
    await axios.post(
      properties.aiqBaseUrl + properties.contractBaseUrl + '/add-spd-coverage',
      {
        matrix_data: matrix,
        eg_nid: egNid,
        plan_id: planId,
      },
    );
  } catch (error) {
    throw error;
  }
};

export const extractAndSaveProvisionMatrixData = async (
  documentUrl: string,
  contractId: string,
  egNid: string,
  employer: string,
  service_agreement_name: string,
  category: string,
  planId?: string,
  draftId?: string,
) => {
  const defaultParameters = await axios.post(
    properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.getDefaultParameters,
    {
      category,
      service_agreement_name,
    },
  );

  const matrixSchema = defaultParameters?.data?.data?.map((item: any) => ({
    key: item.key ?? item?.provision_name?.toLowerCase()?.replace(/\s+/g, '_'),
    name: item.provision_name ?? '',
    description: item.assessment_description ?? '',
    type: item.response_type ?? 'TEXT',
    category: item.category ?? '',
    category_Values: item?.category_values || null,
    // type: 'OBJECT',
    // sub_fields: [
    //   {
    //     key: `${
    //       item.key ?? item?.provision_name?.toLowerCase()?.replace(/\s+/g, '_')
    //     }_value`,
    //     name: item.provision_name ?? '',
    //     description: item.assessment_description ?? '',
    //     type: item.response_type ?? 'TEXT',
    //     enum_values: item?.category_values || null,
    //   },
    //   {
    //     key: `${
    //       item.key ?? item?.provision_name?.toLowerCase()?.replace(/\s+/g, '_')
    //     }_explanation`,
    //     name: 'Explanation',
    //     description: item?.explanation_prompt?.replace(
    //       '{{explanation}}',
    //       item?.explanation || '-',
    //     ),
    //     type: 'TEXT',
    //   },
    // ],
  }));

  let matrix_data: any[] = [];
  let attempts = 0;

  while (attempts < 3) {
    try {
      const response = await extractMetadataDocChain(
        documentUrl,
        '',
        'gpt-5-mini',
        matrixSchema,
      );

      matrix_data = response.answer;
      // matrix_data = response?.answer?.map((item: any) => {
      //   const baseKey = item.key;
      //   const valueKey = `${baseKey}_value`;
      //   const explanationKey = `${baseKey}_explanation`;
      //   return {
      //     key: baseKey,
      //     name: item.name,
      //     description: item.description,
      //     response_type: item.sub_fields?.[0]?.type || null,
      //     result: item.result?.[valueKey] ?? null,
      //     prompt_explanation: item.result?.[explanationKey] ?? null,
      //     evidence: item.evidence?.[valueKey] || [],
      //     confidence_score: item.confidence_score || null,
      //     score_rationale: item.score_rationale || null,
      //   };
      // });

      if (matrix_data && matrix_data.length > 0) {
        break; // successful response, exit loop
      }
    } catch (error) {
      // Optionally log or handle the error
    }

    attempts++;
  }

  if (!matrix_data || matrix_data.length === 0) {
    return; // exit early
  }

  try {
    await axios.post(
      properties.aiqBaseUrl + properties.contractBaseUrl + '/add-matrix',
      {
        eg_nid: egNid,
        ...(contractId ? { contract_id: contractId } : {}),
        ...(planId ? { plan_id: planId } : {}),
        ...(draftId ? { draft_id: draftId } : {}),
        matrix_data,
        ...(category ? { category } : {}),
        ...(service_agreement_name ? { service_agreement_name } : {}),
      },
    );
    (contractId || draftId) &&
      generateReport(
        contractId,
        egNid,
        employer,
        'provisionMatrixReport',
        undefined,
        draftId,
      );
  } catch (error) {
    throw error;
  }
};
